import { ActionTree } from 'vuex';
import { ProjectModule } from './types';
import { RootModule } from '../root-module';
import { crmapi, PossibleUpdates } from '@/types/crm-types';
import { DisplayOptions, LoadProjectOptions, TotalHoursTask, Task } from '@/types/store-types';
import { CrmRepository } from '@/repositories/crm-repository';
import '../../plugins/string-extensions';
import { DayInfo } from '@/models/task-day';

export const actions: ActionTree<ProjectModule, RootModule> = {

  saveDisplayOptions(store, options: DisplayOptions) {
    store.commit('saveDisplayOptions', options);
  },

  async loadProject(store, options: LoadProjectOptions) {
    const repo = (this as any)._vm.$crmApi as CrmRepository;
    const payload = await repo.getProjectAsync(options.projectId, options.startDate, options.endDate);

    store.commit('projectLoaded', payload);
  },

  async loadAdditionalProjectData(store, options: LoadProjectOptions) {
    const repo = (this as any)._vm.$crmApi as CrmRepository;
    const payload = await repo.getProjectAsync(options.projectId, options.startDate, options.endDate);

    store.commit('loadAdditionalProjectData', payload);
  },

  async getEmployees(store) {
    const repo = (this as any)._vm.$crmApi as CrmRepository;
    const payload = await repo.getEmployees();
    store.commit('employeesLoaded', payload);
  },

  projectLoaded(store, payload: crmapi.ProjectData) {
    store.commit('projectLoaded', payload);
  },

  addCategory(store, categoryName: string) {
    store.commit('addCategory', categoryName);
  },

  closeAllWeeks(store, close: boolean) {
    store.commit('closeAllWeeks', close);
  },

  closeWeek(store, day: Date) {
    store.commit('closeWeek', day);
  },

  selectTask(store, id: string) {
    store.commit('selectTask', id);
  },

  async createTask(store, task: Task) {
    const repo = (this as any)._vm.$crmApi as CrmRepository;
    let crmTask: crmapi.Task = {
      taskId: task.taskId === '' ? '00000000-0000-0000-0000-000000000000' : task.taskId,
      name: task.name,
      category: task.category,
      categoryKey: task.categoryKey,
      startDate: task.startDate.toLocalDateString(),
      endDate: task.endDate !== undefined && task.endDate !== null ? task.endDate.toLocalDateString() : undefined,
      employeeId: task.employeeId,
      tariff: task.tariff,
      plannedHours: task.plannedHours,
      possibleActions: task.possibleActions,
    };

    crmTask = await repo.createUpdateTaskAsync(store.state.projectData.projectId, crmTask);
    crmTask.categoryKey = task.categoryKey;

    const startDate = (store.state.weeks && store.state.weeks.length && store.state.weeks[0].firstDay) || new Date();
    const firstDayOfLastWeek = (store.state.weeks && store.state.weeks.length && store.state.weeks[store.state.weeks.length - 1].firstDay) || new Date();
    const endDate = new Date(firstDayOfLastWeek);
    endDate.setDate(endDate.getDate() + 6);

    const employee = await repo.getEmployeeAsync(task.employeeId, startDate, endDate);
    store.commit('ensureEmployee', employee);
    store.commit('createTask', crmTask);
  },

  async updateTask(store, task: Task) {
    const category = store.state.categories.find((c) => c.key === task.categoryKey);

    const repo = (this as any)._vm.$crmApi as CrmRepository;
    let crmTask: crmapi.Task = {
      taskId: task.taskId,
      name: task.name,
      categoryKey: task.categoryKey,
      category: (category && category.name) || '',
      startDate: task.startDate.toLocalDateString(),
      endDate: task.endDate !== undefined && task.endDate !== null ? task.endDate.toLocalDateString() : undefined,
      employeeId: task.employeeId,
      tariff: task.tariff,
      plannedHours: task.plannedHours,
      possibleActions: task.possibleActions,
    };
    crmTask = await repo.createUpdateTaskAsync(store.state.projectData.projectId, crmTask);
    crmTask.categoryKey = task.categoryKey;

    const startDate = (store.state.weeks && store.state.weeks.length && store.state.weeks[0].firstDay) || new Date();
    const firstDayOfLastWeek = (store.state.weeks && store.state.weeks.length && store.state.weeks[store.state.weeks.length - 1].firstDay) || new Date();
    const endDate = new Date(firstDayOfLastWeek);
    endDate.setDate(endDate.getDate() + 6);

    const employee = await repo.getEmployeeAsync(task.employeeId, startDate, endDate);

    store.commit('ensureEmployee', employee);
    store.commit('updateTask', crmTask);
  },

  async updateDay(store, day: DayInfo) {
    const repo = (this as any)._vm.$crmApi as CrmRepository;
    const crmDay: crmapi.Day = {
      taskId: day.taskId,
      taskDayId: day.taskDayId === '' ? '00000000-0000-0000-0000-000000000000' : day.taskDayId,
      day: day.day.toLocalDateString(),
      plannedHours: day.plannedHours,
      available: day.available,
    };

    const response = await repo.createUpdateDeleteTaskDayAsync(crmDay.taskId, crmDay);
    if (response.projectTaskDay) {
      store.commit('updateDay', response.projectTaskDay);
    } else if (day.taskDayId !== '' && day.plannedHours === 0) {
      store.commit('deleteDay', day.taskDayId);
    }
    const totalHoursTask: TotalHoursTask = {
      taskId: day.taskId,
      totalHours: response.plannedHours,
    };
    store.commit('updateTaskPlannedHours', totalHoursTask);
  },

  async deleteSelectedTasks(store) {
    const repo = (this as any)._vm.$crmApi as CrmRepository;
    while (store.state.selectedTasks.length > 0) {
      await repo.deleteTaskAsync(store.state.projectData.projectId, store.state.selectedTasks[0]);
      store.commit('deleteTask', store.state.selectedTasks[0]);
    }
  },
};
